import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router";
import { Box, Button, ButtonBase, Link, Typography, useTheme } from "@mui/material";
import { GoArrowRight } from "react-icons/go";
import { observer } from "mobx-react";

import CoreVirtualizedTable from "../../core/CoreVirtualizedTable";
import CoreKeywordSearch from "../../core/CoreKeywordSearch";
import { CloseIcon } from "../../../icons";
import ColumnVisibilityButton from "../../../claims/components/filters-components/ColumnVisibilityButton";
import { getClaimsTableColumns } from "../../../claims/claimsTableColumns";
import { useClaimsStore } from "../../../../hooks/useClaimsStore";
import { useMainStore } from "../../../../hooks/useMainStore";
import useNotifier from "../../../../hooks/useNotifier";
import { apiService } from "../../../../services";
import {
  loadPersistedHomeClaimsFilters,
  savePersistedHomeClaimsFilters,
} from "../../../helpers/homeClaimsFiltersStorage";
import {
  CLAIMS_FILTERABLE_COLUMNS,
  getClaimsColumnFilterState,
  getClaimsColumnOptions,
  withClaimsColumnFilter,
} from "../../../claims/claimsColumnFilters";
import { OrderBy } from "../../../../stores/ClaimsStore";
import {
  AllFilters,
  Claim,
  CLAIMS_COLUMN_FILTER_TYPE,
  CLAIMS_TABLE_COLUMN_OPTIONS,
  CLAIMS_TABLE_COLUMN_ORDER,
  ClaimsTableColumnId,
  ClaimsTableColumnSetting,
} from "../../../../types/claims";
import {
  ColumnFiltersConfig,
  SimpleColumnDef,
  SortingState,
} from "../../../../types/table";

const PAGE_SIZE = 20;
const ORDER_BY: OrderBy = { field: "updatedAt", direction: "desc" };

// The rows are already scoped to the current user, so the Assignee column
// isn't offered here.
const EXCLUDED_COLUMNS = ["assignedUser"];
const COLUMN_OPTIONS = CLAIMS_TABLE_COLUMN_OPTIONS.filter(
  (col) => !EXCLUDED_COLUMNS.includes(col.id),
);

// The Claims table's filterable columns, less the excluded ones.
const FILTERABLE_COLUMNS = CLAIMS_FILTERABLE_COLUMNS.filter(
  (columnId) => !EXCLUDED_COLUMNS.includes(columnId),
);

// The columns this table showed before it offered the rest of the Claims
// table's; everything else starts hidden.
const DEFAULT_VISIBLE_COLUMNS: ClaimsTableColumnId[] = [
  "claimNumber",
  "patientName",
  "medicalRecordNumber",
  "dateOfService",
  "billedAmount",
  "status",
];

const getDefaultColumnSettings = (): ClaimsTableColumnSetting[] =>
  COLUMN_OPTIONS.map((col) => ({
    id: col.id,
    visible: DEFAULT_VISIBLE_COLUMNS.includes(col.id),
    required: col.required === true,
  }));

// Saved settings over the defaults, so columns added since are picked up.
const mergeColumnSettings = (
  saved: ClaimsTableColumnSetting[] | null,
): ClaimsTableColumnSetting[] => {
  const defaults = getDefaultColumnSettings();
  if (!saved) return defaults;
  const savedById = new Map(
    saved
      .filter((setting) => setting && typeof setting === "object")
      .map((setting) => [setting.id, setting]),
  );
  return defaults.map((def) => ({ ...def, ...savedById.get(def.id) }));
};

const isMandatoryColumn = (columnId: string) =>
  !!CLAIMS_TABLE_COLUMN_OPTIONS.find((col) => col.id === columnId)?.mandatory;

export const ExtendedHomeClaimsSection = observer(() => {
  const theme = useTheme();
  const claimsStore = useClaimsStore();

  // Backend filter options for the column filters (see getColumnOptions).
  // Not gated on isOptionsLoading: the store initializes it (and
  // clearClaimsStore resets it) to true, so it can't tell "in flight" from
  // "never loaded" and would skip this load unless /claims was visited first.
  useEffect(() => {
    if (Object.keys(claimsStore.claimsFilterOptions).length === 0) {
      claimsStore.loadClaimsFilterOptions();
    }
  }, [claimsStore]);

  const notifier = useNotifier();

  const [claimsData, setClaimsData] = useState<Claim[] | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  // Sort, column filters and keyword search are local to this section, not
  // ClaimsStore's: the store's are the Claims page's, and narrowing the home
  // table must not change what /claims shows. They're persisted separately
  // ("filters:home"), restored once on mount. The backend ANDs all of them
  // with the current-user scope (user-data endpoint).
  const { filtersScope } = useMainStore();
  const [persisted] = useState(() => loadPersistedHomeClaimsFilters(filtersScope));
  const [orderBy, setOrderBy] = useState<OrderBy | null>(
    persisted.orderBy === undefined ? ORDER_BY : persisted.orderBy,
  );
  // A saved filter on an excluded column would be hidden and uncleareable.
  const [filters, setFilters] = useState<AllFilters | null>(() =>
    EXCLUDED_COLUMNS.reduce<AllFilters | null>(
      (acc, columnId) => withClaimsColumnFilter(acc, columnId, []),
      persisted.filters,
    ),
  );
  const [keywordSearch, setKeywordSearch] = useState(persisted.keywordSearch);
  const [columnSettings, setColumnSettings] = useState(() =>
    mergeColumnSettings(persisted.columnSettings),
  );

  // Skip the mount run: it would only write back what was just restored.
  const isFirstPersistRunRef = useRef(true);
  useEffect(() => {
    if (isFirstPersistRunRef.current) {
      isFirstPersistRunRef.current = false;
      return;
    }
    savePersistedHomeClaimsFilters(filtersScope, {
      keywordSearch,
      filters,
      orderBy,
      columnSettings,
    });
    // Not on filtersScope: a scope change alone must not save this scope's
    // filters under another one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keywordSearch, filters, orderBy, columnSettings]);

  // Column visibility and pins as on the Claims page (ClaimsStore): Claim # is
  // always shown and pinned, and pinning a hidden column shows it.
  const isColumnVisible = useCallback(
    (columnId: string) =>
      isMandatoryColumn(columnId) ||
      columnSettings.find((setting) => setting.id === columnId)?.visible !== false,
    [columnSettings],
  );

  const isColumnRequired = useCallback(
    (columnId: string) =>
      isMandatoryColumn(columnId) ||
      !!columnSettings.find((setting) => setting.id === columnId)?.required,
    [columnSettings],
  );

  const updateColumnSetting = useCallback(
    (
      columnId: ClaimsTableColumnId,
      update: (setting: ClaimsTableColumnSetting) => Partial<ClaimsTableColumnSetting>,
    ) => {
      if (isMandatoryColumn(columnId)) return;
      setColumnSettings((prev) =>
        prev.map((setting) =>
          setting.id === columnId ? { ...setting, ...update(setting) } : setting,
        ),
      );
    },
    [],
  );

  const toggleColumnVisibility = useCallback(
    (columnId: ClaimsTableColumnId) =>
      updateColumnSetting(columnId, (setting) => ({ visible: !setting.visible })),
    [updateColumnSetting],
  );

  const toggleRequiredColumn = useCallback(
    (columnId: ClaimsTableColumnId) =>
      updateColumnSetting(columnId, (setting) =>
        setting.required ? { required: false } : { required: true, visible: true },
      ),
    [updateColumnSetting],
  );

  // Same rule as ClaimsStore.hasFiltersApplied: a non-default sort counts too.
  const hasFiltersApplied =
    !!keywordSearch.trim() ||
    !!filters ||
    orderBy?.field !== ORDER_BY.field ||
    orderBy?.direction !== ORDER_BY.direction;

  const clearFilters = useCallback(() => {
    setKeywordSearch("");
    setFilters(null);
    setOrderBy(ORDER_BY);
    // As ClaimsStore.clearFilters: column visibility goes back to the defaults,
    // pins stay.
    const defaults = getDefaultColumnSettings();
    setColumnSettings((prev) =>
      prev.map((setting) => ({
        ...setting,
        visible:
          defaults.find((def) => def.id === setting.id)?.visible ?? setting.visible,
      })),
    );
  }, []);

  const fetchPage = useCallback(
    (page: number) =>
      apiService.getClaimsData(
        page,
        PAGE_SIZE,
        orderBy,
        filters ?? undefined,
        keywordSearch.trim() || undefined,
        undefined,
        true,
      ),
    [orderBy, filters, keywordSearch],
  );

  // Bumped on every new query (sort/filters/search change) so an in-flight
  // loadMore response from a stale query can detect it's no longer current
  // and skip applying its (now-mismatched) page/results.
  const queryIdRef = useRef(0);

  useEffect(() => {
    const queryId = ++queryIdRef.current;
    setIsLoading(true);
    // An in-flight loadMore from the previous query won't clear this itself
    // (its finally is guarded by queryId), so reset it here or infinite
    // scroll stays blocked for the new query.
    setIsLoadingMore(false);
    setCurrentPage(0);
    setHasMore(true);

    fetchPage(0)
      .then((response) => {
        if (queryId !== queryIdRef.current) return;
        setClaimsData(response.claims);
        setHasMore(response.hasMore);
      })
      .catch(() => {
        if (queryId !== queryIdRef.current) return;
        setClaimsData(null);
        setHasMore(false);
        notifier.showError("Failed to load claims");
      })
      .finally(() => {
        if (queryId === queryIdRef.current) setIsLoading(false);
      });
  }, [fetchPage]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;

    const queryId = queryIdRef.current;
    setIsLoadingMore(true);
    const nextPage = currentPage + 1;

    fetchPage(nextPage)
      .then((response) => {
        if (queryId !== queryIdRef.current) return;
        setClaimsData((prev) =>
          prev ? [...prev, ...response.claims] : response.claims,
        );
        setCurrentPage(nextPage);
        setHasMore(response.hasMore);
      })
      .catch(() => {
        if (queryId !== queryIdRef.current) return;
        // Leave hasMore as-is so the user can retry by scrolling again,
        // instead of silently ending pagination on a transient failure.
        notifier.showError("Failed to load more claims");
      })
      .finally(() => {
        if (queryId === queryIdRef.current) setIsLoadingMore(false);
      });
  }, [currentPage, fetchPage, hasMore, isLoadingMore, notifier]);

  const sorting: SortingState = useMemo(
    () => (orderBy ? [{ id: orderBy.field, desc: orderBy.direction === "desc" }] : []),
    [orderBy],
  );

  const handleSortingChange = useCallback((newSorting: SortingState) => {
    if (newSorting.length === 0) {
      setOrderBy(null);
    } else {
      const { id, desc } = newSorting[0];
      setOrderBy({ field: id, direction: desc ? "desc" : "asc" });
    }
  }, []);

  // Filter options as on the Claims table (ClaimsStore.getUniqueColumnValues).
  const getColumnOptions = useCallback(
    (columnId: string): string[] =>
      getClaimsColumnOptions(columnId, {
        filterOptions: claimsStore.claimsFilterOptions,
        filters,
        claims: claimsData,
      }),
    [claimsStore, claimsData, filters],
  );

  const columnFiltersConfig: ColumnFiltersConfig = useMemo(
    () => ({
      filters: Object.fromEntries(
        FILTERABLE_COLUMNS.map((columnId) => [
          columnId,
          getClaimsColumnFilterState(filters, columnId),
        ]),
      ),
      getOptions: getColumnOptions,
      onApply: (columnId, values, operator) =>
        setFilters((prev) => withClaimsColumnFilter(prev, columnId, values, operator)),
      onClear: (columnId) =>
        setFilters((prev) => withClaimsColumnFilter(prev, columnId, [])),
      filterableColumns: FILTERABLE_COLUMNS,
      columnFilterTypes: CLAIMS_COLUMN_FILTER_TYPE,
      isColumnPinned: isColumnRequired,
      onTogglePin: (columnId) =>
        toggleRequiredColumn(columnId as ClaimsTableColumnId),
      canPinColumn: (columnId) => !isMandatoryColumn(columnId),
    }),
    [filters, getColumnOptions, isColumnRequired, toggleRequiredColumn],
  );

  const navigate = useNavigate();

  // ClaimsStore's keyword search (the "Search claims..." input) and column
  // filters outlive the Claims page (clearClaimsStore leaves them alone), so
  // the search set here is picked up when /claims mounts. Drop any leftover
  // column filters/tab first so they can't hide the clicked claim.
  const openClaimInClaimsPage = useCallback(
    (claimNumber: string) => {
      claimsStore.clearAllColumnFilters();
      claimsStore.setActiveTab("all");
      claimsStore.setClaimKeywordSearch(claimNumber);
      navigate("/claims");
    },
    [claimsStore, navigate],
  );

  // The Claims table's columns, except Claim # opens the claim on /claims
  // and the data-warnings icon column and excluded columns are left out.
  const columns: SimpleColumnDef<Claim>[] = useMemo(
    () =>
      getClaimsTableColumns()
        .filter(
          (column) =>
            column.id !== "warnings" && !EXCLUDED_COLUMNS.includes(column.id),
        )
        .map((column) =>
          column.id !== "claimNumber"
            ? column
            : {
                ...column,
                cell: (value) =>
                  value ? (
                    <Link
                      component="button"
                      type="button"
                      underline="hover"
                      onClick={(event) => {
                        event.stopPropagation();
                        openClaimInClaimsPage(value);
                      }}
                      sx={{
                        display: "block",
                        maxWidth: "100%",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        textAlign: "left",
                        fontWeight: 600,
                        color: "primary.main",
                        fontSize: "14px",
                      }}
                    >
                      {value}
                    </Link>
                  ) : (
                    <Typography>-</Typography>
                  ),
              },
        ),
    [openClaimInClaimsPage],
  );

  // Visible columns in the Claims table's order, pinned ones first.
  const visibleColumns = useMemo(() => {
    const columnsById = new Map(columns.map((column) => [column.id, column]));
    const visibleIds = CLAIMS_TABLE_COLUMN_ORDER.filter(isColumnVisible);
    return [
      ...visibleIds.filter((id) => isColumnRequired(id)),
      ...visibleIds.filter((id) => !isColumnRequired(id)),
    ]
      .map((id) => columnsById.get(id))
      .filter((column): column is SimpleColumnDef<Claim> => !!column);
  }, [columns, isColumnVisible, isColumnRequired]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 1.5,
        width: "100%",
        minWidth: 0,
        padding: 2,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "15px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          px: 1,
        }}
      >
        <Typography
          variant="h5"
          sx={{ color: "text.primary", fontSize: "16px", flexShrink: 0 }}
        >
          Your Claims
        </Typography>

        {hasFiltersApplied ? (
          <Button
            variant="text"
            startIcon={<CloseIcon />}
            onClick={clearFilters}
            sx={{ ml: "auto", flexShrink: 0, color: "error.dark" }}
          >
            Clear Filters
          </Button>
        ) : null}

        <Box
          sx={{
            flex: 1,
            minWidth: "160px",
            maxWidth: "260px",
            ml: hasFiltersApplied ? 0 : "auto",
          }}
        >
          <CoreKeywordSearch
            showSearchIcon
            placeholder="Search your claims..."
            value={keywordSearch}
            onChange={(value) => setKeywordSearch(value)}
            sx={{ "& .MuiInputBase-root": { height: "36px" } }}
          />
        </Box>

        <ColumnVisibilityButton
          columns={COLUMN_OPTIONS}
          isColumnVisible={isColumnVisible}
          toggleColumnVisibility={toggleColumnVisibility}
          isColumnRequired={isColumnRequired}
          toggleRequiredColumn={toggleRequiredColumn}
        />

        <ButtonBase
          component={NavLink}
          to="/claims"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            borderRadius: "8px",
            px: 1,
            py: 0.5,
            color: theme.palette.custom.brandTeal,
          }}
        >
          <Typography
            sx={{ fontWeight: 600, fontSize: "13px", color: "inherit" }}
          >
            View all claims
          </Typography>
          <GoArrowRight className="w-4 h-4 shrink-0" color="inherit" />
        </ButtonBase>
      </Box>

      <Box
        sx={{
          borderRadius: "8px",
          border: "1px solid",
          borderColor: "divider",
          overflow: "hidden",
        }}
      >
        <CoreVirtualizedTable<Claim>
          isLoading={isLoading}
          data={claimsData || []}
          columns={visibleColumns}
          height="360px"
          minWidth={500}
          getRowId={(row) => row.id}
          emptyMessage="No claims found"
          onLoadMore={loadMore}
          hasMore={hasMore}
          isLoadingMore={isLoadingMore}
          sorting={sorting}
          onSortingChange={handleSortingChange}
          columnFilters={columnFiltersConfig}
          pinColumns
          disableFirstOfTypeStyles
          disableLastOfTypeStyles
        />
      </Box>
    </Box>
  );
});
